import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { DateRange } from 'react-day-picker';
import { dataService } from '@/services/data-service';
import type { AuditLog, AuditLogFilter } from '@/types/audit';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Label } from '@/components/ui/label';
import { 
  Download, 
  FileText, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  Search,
  FileSpreadsheet,
  Settings2,
  Mail
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { formatFieldName, formatActionType } from '@/lib/format-utils';
import { ConfigureColumnsDialog } from '@/components/configure-columns-dialog';
import { useColumnConfig } from '@/hooks/use-column-config';
import { DEFAULT_AUDIT_COLUMNS, AUDIT_COLUMNS_STORAGE_KEY } from '@/lib/audit-columns-config';
import { exportAuditLogsToPDF, downloadPDF, getPDFBlob } from '@/lib/pdf-export';
import { EmailDialog, type EmailData } from '@/components/email-dialog';

export default function AuditPage(): React.ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [actionTypes, setActionTypes] = useState<string[]>([]);
  const [users, setUsers] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [configureColumnsOpen, setConfigureColumnsOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);

  // Column configuration with persistence
  const {
    columns: columnConfigs,
    columnVisibility,
    visibleColumns,
    updateColumns,
    resetColumns,
  } = useColumnConfig({
    storageKey: AUDIT_COLUMNS_STORAGE_KEY,
    defaultColumns: DEFAULT_AUDIT_COLUMNS,
  });
  
  // Filter state
  const [filters, setFilters] = useState<AuditLogFilter>({
    assetId: searchParams.get('assetId') || undefined,
    startDate: searchParams.get('startDate') || undefined,
    endDate: searchParams.get('endDate') || undefined,
    actionType: searchParams.get('actionType') || undefined,
    user: searchParams.get('user') || undefined,
    page: parseInt(searchParams.get('page') || '1'),
    pageSize: 50,
    sortBy: 'Timestamp',
    sortOrder: 'desc'
  });

  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadAuditLogs();
    loadActionTypes();
    loadUsers();
  }, [filters]);

  const loadAuditLogs = async () => {
    try {
      setLoading(true);
      const result = await dataService.getAuditLogs(filters);
      setAuditLogs(result.items);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const loadActionTypes = async () => {
    try {
      const types = await dataService.getAuditActionTypes();
      setActionTypes(types);
    } catch (error) {
      console.error('Failed to load action types:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const userList = await dataService.getAuditUsers();
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const applyFilters = () => {
    setFilters({ ...tempFilters, page: 1 });
    setShowFilters(false);
    
    // Update URL params
    const params = new URLSearchParams();
    if (tempFilters.assetId) params.set('assetId', tempFilters.assetId);
    if (tempFilters.startDate) params.set('startDate', tempFilters.startDate);
    if (tempFilters.endDate) params.set('endDate', tempFilters.endDate);
    if (tempFilters.actionType) params.set('actionType', tempFilters.actionType);
    if (tempFilters.user) params.set('user', tempFilters.user);
    params.set('page', '1');
    setSearchParams(params);
  };

  const clearFilters = () => {
    const clearedFilters: AuditLogFilter = {
      page: 1,
      pageSize: 50,
      sortBy: 'Timestamp',
      sortOrder: 'desc'
    };
    setTempFilters(clearedFilters);
    setFilters(clearedFilters);
    setSearchParams(new URLSearchParams());
    setSearchQuery('');
  };

  const handleExport = async (format: 'csv' | 'pdf' | 'excel') => {
    try {
      if (format === 'pdf') {
        handleExportPDF();
      } else {
        console.log(`Exporting to ${format.toUpperCase()}...`);
        toast.success(`Export to ${format.toUpperCase()} started`);
        // TODO: Implement CSV and Excel export functionality
        // await dataService.exportAuditLogs(filters, format);
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export audit trail');
    }
  };

  const handleExportPDF = () => {
    try {
      // Get visible column IDs
      const visibleColumnIds = visibleColumns.map(col => col.id);
      
      // Generate PDF
      const doc = exportAuditLogsToPDF({
        title: 'Audit Trail Report',
        data: filteredAuditLogs,
        visibleColumns: visibleColumnIds,
        filters: {
          assetId: filters.assetId,
          startDate: filters.startDate,
          endDate: filters.endDate,
          actionType: filters.actionType,
          user: filters.user,
        },
      });

      // Download PDF
      const filename = `audit-trail-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.pdf`;
      downloadPDF(doc, filename);
      
      toast.success('PDF exported successfully');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to export PDF');
    }
  };

  const handleEmailPDF = async () => {
    try {
      // Get visible column IDs
      const visibleColumnIds = visibleColumns.map(col => col.id);
      
      // Generate PDF
      const doc = exportAuditLogsToPDF({
        title: 'Audit Trail Report',
        data: filteredAuditLogs,
        visibleColumns: visibleColumnIds,
        filters: {
          assetId: filters.assetId,
          startDate: filters.startDate,
          endDate: filters.endDate,
          actionType: filters.actionType,
          user: filters.user,
        },
      });

      // Get PDF blob
      const blob = getPDFBlob(doc);
      setPdfBlob(blob);
      
      // Open email dialog
      setEmailDialogOpen(true);
    } catch (error) {
      console.error('Failed to prepare PDF for email:', error);
      toast.error('Failed to prepare PDF for email');
    }
  };

  const handleSendEmail = async (emailData: EmailData) => {
    try {
      if (!pdfBlob) {
        throw new Error('PDF not generated');
      }

      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(pdfBlob);
      
      await new Promise((resolve, reject) => {
        reader.onloadend = () => {
          const base64data = reader.result as string;
          
          // TODO: Call API to send email with PDF attachment
          console.log('Sending email with PDF:', {
            ...emailData,
            attachment: base64data.substring(0, 100) + '...',
          });
          
          // Simulate API call
          setTimeout(() => {
            resolve(true);
          }, 1000);
        };
        reader.onerror = reject;
      });

      toast.success('Email sent successfully');
    } catch (error) {
      console.error('Failed to send email:', error);
      throw error;
    }
  };

  const getActionTypeBadgeStyle = (actionType: string) => {
    const normalizedType = (actionType ?? '').toLowerCase().replace(/\s+/g, '');
    
    // Subtle pastel colors for light mode
    switch (normalizedType) {
      case 'create':
        return {
          backgroundColor: 'rgb(220 252 231)', // light green
          color: 'rgb(22 101 52)', // dark green text
          borderColor: 'rgb(187 247 208)',
        };
      case 'edit':
      case 'update':
        return {
          backgroundColor: 'rgb(219 234 254)', // light blue
          color: 'rgb(30 64 175)', // dark blue text
          borderColor: 'rgb(191 219 254)',
        };
      case 'delete':
        return {
          backgroundColor: 'rgb(254 226 226)', // light red
          color: 'rgb(153 27 27)', // dark red text
          borderColor: 'rgb(254 202 202)',
        };
      case 'move':
      case 'transfer':
        return {
          backgroundColor: 'rgb(243 232 255)', // light purple
          color: 'rgb(107 33 168)', // dark purple text
          borderColor: 'rgb(233 213 255)',
        };
      case 'statuschange':
        return {
          backgroundColor: 'rgb(255 237 213)', // light orange
          color: 'rgb(154 52 18)', // dark orange text
          borderColor: 'rgb(254 215 170)',
        };
      case 'valuechange':
        return {
          backgroundColor: 'rgb(254 249 195)', // light yellow
          color: 'rgb(133 77 14)', // dark yellow/brown text
          borderColor: 'rgb(254 240 138)',
        };
      default:
        return {
          backgroundColor: 'rgb(243 244 246)', // light gray
          color: 'rgb(55 65 81)', // dark gray text
          borderColor: 'rgb(229 231 235)',
        };
    }
  };

  const getActionTypeBadgeStyleDark = (actionType: string) => {
    const normalizedType = (actionType ?? '').toLowerCase().replace(/\s+/g, '');
    
    // Dark mode versions with adjusted colors
    switch (normalizedType) {
      case 'create':
        return {
          backgroundColor: 'rgb(20 83 45)', // dark green bg
          color: 'rgb(187 247 208)', // light green text
          borderColor: 'rgb(34 197 94)',
        };
      case 'edit':
      case 'update':
        return {
          backgroundColor: 'rgb(30 58 138)', // dark blue bg
          color: 'rgb(191 219 254)', // light blue text
          borderColor: 'rgb(59 130 246)',
        };
      case 'delete':
        return {
          backgroundColor: 'rgb(127 29 29)', // dark red bg
          color: 'rgb(254 202 202)', // light red text
          borderColor: 'rgb(239 68 68)',
        };
      case 'move':
      case 'transfer':
        return {
          backgroundColor: 'rgb(88 28 135)', // dark purple bg
          color: 'rgb(233 213 255)', // light purple text
          borderColor: 'rgb(168 85 247)',
        };
      case 'statuschange':
        return {
          backgroundColor: 'rgb(124 45 18)', // dark orange bg
          color: 'rgb(254 215 170)', // light orange text
          borderColor: 'rgb(249 115 22)',
        };
      case 'valuechange':
        return {
          backgroundColor: 'rgb(113 63 18)', // dark yellow/brown bg
          color: 'rgb(254 240 138)', // light yellow text
          borderColor: 'rgb(234 179 8)',
        };
      default:
        return {
          backgroundColor: 'rgb(55 65 81)', // dark gray bg
          color: 'rgb(229 231 235)', // light gray text
          borderColor: 'rgb(107 114 128)',
        };
    }
  };

  // Filter audit logs by search query
  const filteredAuditLogs = auditLogs.filter((log) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      log.assetCode?.toLowerCase().includes(query) ||
      log.assetName?.toLowerCase().includes(query) ||
      log.user?.toLowerCase().includes(query) ||
      log.actionType?.toLowerCase().includes(query) ||
      log.field?.toLowerCase().includes(query) ||
      log.oldValue?.toLowerCase().includes(query) ||
      log.newValue?.toLowerCase().includes(query)
    );
  });

  const activeFilterCount = [
    filters.assetId,
    filters.startDate,
    filters.endDate,
    filters.actionType,
    filters.user
  ].filter(Boolean).length;

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <FileText className="h-6 w-6 text-primary" />
              Audit Trail
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Complete history of all system changes and user actions
            </p>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="border-b bg-card p-4 mt-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-1 items-center gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search audit logs..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="mr-2 h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfigureColumnsOpen(true)}
            >
              <Settings2 className="mr-2 h-4 w-4" />
              Columns
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Export as</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <FileText className="mr-2 h-4 w-4" />
                  PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')}>
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Excel
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleEmailPDF}>
                  <Mail className="mr-2 h-4 w-4" />
                  Email PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 rounded-md border bg-background p-4">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Asset ID Filter */}
                <div className="space-y-2">
                  <Label>Asset ID</Label>
                  <Input
                    placeholder="Enter asset ID..."
                    value={tempFilters.assetId || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setTempFilters({ ...tempFilters, assetId: e.target.value || undefined })}
                  />
                </div>

                {/* Date Range Filter */}
                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <DateRangePicker
                    dateRange={{
                      from: tempFilters.startDate ? new Date(tempFilters.startDate) : undefined,
                      to: tempFilters.endDate ? new Date(tempFilters.endDate) : undefined,
                    }}
                    onDateRangeChange={(range) => {
                      setTempFilters({
                        ...tempFilters,
                        startDate: range?.from ? format(range.from, 'yyyy-MM-dd') : undefined,
                        endDate: range?.to ? format(range.to, 'yyyy-MM-dd') : undefined,
                      });
                    }}
                    placeholder="Select date range"
                  />
                </div>

                {/* Action Type Filter */}
                <div className="space-y-2">
                  <Label>Action Type</Label>
                  <Select
                    value={tempFilters.actionType || 'all'}
                    onValueChange={(value: string) => setTempFilters({ ...tempFilters, actionType: value === 'all' ? undefined : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All actions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All actions</SelectItem>
                      {actionTypes.map((type) => (
                        <SelectItem key={type} value={type}>
                          {formatActionType(type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* User Filter */}
                <div className="space-y-2">
                  <Label>User</Label>
                  <Select
                    value={tempFilters.user || 'all'}
                    onValueChange={(value: string) => setTempFilters({ ...tempFilters, user: value === 'all' ? undefined : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All users" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All users</SelectItem>
                      {users.map((user) => (
                        <SelectItem key={user} value={user}>
                          {user}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={clearFilters}>
                  Clear All
                </Button>
                <Button onClick={applyFilters}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-2">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Loading audit logs...</p>
            </div>
          </div>
        ) : filteredAuditLogs.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold">No audit records found</h3>
              <p className="text-sm text-muted-foreground mt-2">
                Try adjusting your filters or search query
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-md border bg-white dark:bg-black">
            <Table containerClassName="max-h-[calc(100vh-258px)]">
              <TableHeader>
                <TableRow>
                  {visibleColumns.map((column) => (
                    <TableHead key={column.id}>
                      {column.label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAuditLogs.map((log) => (
                  <TableRow key={log.id}>
                    {visibleColumns.map((column) => {
                      switch (column.id) {
                        case 'timestamp':
                          return (
                            <TableCell key={column.id} className="font-mono text-sm">
                              {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss')}
                            </TableCell>
                          );
                        case 'assetCode':
                          return (
                            <TableCell key={column.id} className="font-mono">
                              {log.assetCode}
                            </TableCell>
                          );
                        case 'assetName':
                          return (
                            <TableCell key={column.id}>
                              {log.assetName}
                            </TableCell>
                          );
                        case 'user':
                          return (
                            <TableCell key={column.id}>
                              {log.user}
                            </TableCell>
                          );
                        case 'actionType':
                          return (
                            <TableCell key={column.id}>
                              <span 
                                className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold pointer-events-none"
                                style={
                                  document.documentElement.classList.contains('dark')
                                    ? getActionTypeBadgeStyleDark(log.actionType)
                                    : getActionTypeBadgeStyle(log.actionType)
                                }
                              >
                                {formatActionType(log.actionType)}
                              </span>
                            </TableCell>
                          );
                        case 'field':
                          return (
                            <TableCell key={column.id}>
                              {formatFieldName(log.field)}
                            </TableCell>
                          );
                        case 'changes':
                          return (
                            <TableCell key={column.id} className="max-w-xs">
                              {log.oldValue && log.newValue ? (
                                <span className="text-sm">
                                  <span className="text-red-600 dark:text-red-400">{log.oldValue}</span>
                                  {' → '}
                                  <span className="text-green-600 dark:text-green-400">{log.newValue}</span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                          );
                        default:
                          return <TableCell key={column.id}>-</TableCell>;
                      }
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Pagination and Summary */}
        {!loading && filteredAuditLogs.length > 0 && (
          <div className="mt-4 space-y-4">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div>
                Showing {filteredAuditLogs.length} of {totalCount} entries
                {searchQuery && filteredAuditLogs.length !== auditLogs.length && ` (filtered from ${auditLogs.length})`}
              </div>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ ...filters, page: 1 })}
                  disabled={filters.page === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === 1}
                  onClick={() => setFilters({ ...filters, page: filters.page! - 1 })}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">
                    Page {filters.page} of {totalPages}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page === totalPages}
                  onClick={() => setFilters({ ...filters, page: filters.page! + 1 })}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setFilters({ ...filters, page: totalPages })}
                  disabled={filters.page === totalPages}
                >
                  Last
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Configure Columns Dialog */}
      <ConfigureColumnsDialog
        open={configureColumnsOpen}
        onOpenChange={setConfigureColumnsOpen}
        columns={columnConfigs}
        onColumnsChange={updateColumns}
        onReset={resetColumns}
      />

      {/* Email Dialog */}
      <EmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        onSend={handleSendEmail}
        defaultSubject="Audit Trail Report"
        defaultMessage="Please find attached the Audit Trail report."
      />
    </div>
  );
}
